// store 
import * as ProtocolsStore from '../../../redux/middleware/MedicalProtocolMiddleware';
import * as ProtocolConsumptionStore from '../../../redux/middleware/ProtocolConsumptionMiddleware';

// state
import { AppState } from '../../../redux/reducers';

// models
import { Protocol } from '../../../models/Entities/Protocols/Protocol';
import { ProtocolConsumption } from '../../../models/Entities/Protocols/ProtocolConsumption';
import { Contract } from '../../../models/Entities/Contracts/Contract';
import { ContractProtocolAssociation } from '../../../models/Entities/Contracts/ContractProtocolAssociation';


//#region Props

interface IProps {
    ProtocolId: number;
    status: number;
}

interface ConnectedProps {
    isLoading: boolean;
    successLoading: boolean;
    error: string | null;
    Consumptions: ProtocolConsumption[];
    TotalByCurrency: {Total: number, Currency: string, TotalOwner: number, TotalTeam: number}[];
    Totals: {ConceptName: string, CurrencyValue: string, Total: number, TotalOwner: number, TotalTeam: number}[]
    protocol: Protocol | null;
    contract : Contract | null;
}

interface MedicalServicesDispatchProps {
    GetMedicalProtocolById: typeof ProtocolsStore.actionCreators.GetMedicalProtocolById;
    GetTotalsByStatus: typeof ProtocolConsumptionStore.ActionCreators.GetTotalsByStatus;
    GetProtocolConsumptionToSettle: typeof ProtocolConsumptionStore.ActionCreators.GetProtocolConsumptionToSettle;
    GetAllProtocolContractAssociations: typeof ProtocolsStore.actionCreators.GetAllProtocolContractAssociations;
}

export type Props = IProps & ConnectedProps & MedicalServicesDispatchProps;

const calculateConceptNetAmounts = (consumption: ProtocolConsumption, conceptValue: ProtocolConsumption['conceptValues'][number]) => {
    const hasPercentage = conceptValue.percentValue !== undefined && conceptValue.percentValue !== null;
    const grossFromPercentage = hasPercentage ? ((consumption.amount ?? 0) * (conceptValue.percentValue ?? 0)) / 100 : undefined;
    const grossValue = grossFromPercentage ?? conceptValue.value ?? 0;

    const ownerPct = conceptValue.feeDivisionPercentage === 0 ? 100 : (conceptValue.feeDivisionPercentage ?? 0);
    const teamPct = conceptValue.teamDivisionPercentage ?? 0;

    const ownerGross = (grossValue * ownerPct) / 100;
    const teamGross = (grossValue * teamPct) / 100;

    const ownerWithholding = conceptValue.ownnerWithholdingAmount ?? 0;
    const teamWithholding = conceptValue.teamWithholdingAmount ?? 0;

    const ownerNet = conceptValue.applyWithholding ? ownerGross - ownerWithholding : ownerGross;
    const teamNet = conceptValue.applyWithholding ? teamGross - teamWithholding : teamGross;

    return {
        ownerNet,
        teamNet,
        totalNet: ownerNet + teamNet,
    };
};

export const mapStateToProps = (state: AppState, ownProps: IProps) => {

    const AuxProtocol: Protocol | null =  state.MedicalProtocols?.list?.find((protocol: Protocol) => protocol.id === ownProps.ProtocolId) || null;

    const AuxContract: Contract | null = state.Contract?.list?.find((contract: Contract) => 
        contract.protocol?.find((protocol: ContractProtocolAssociation) => 
            protocol.protocolId === ownProps.ProtocolId)) || null;

    const AuxConsumptions: ProtocolConsumption[] = AuxProtocol?.consumptionsToSettle?.filter((consumption: ProtocolConsumption) => consumption.status === 6) || [];

    // calculate total values to settle by currency (main + additional concept values, net of withholding)
    const AuxConceptTotalByCurrency: {Total: number, Currency: string, TotalOwner: number, TotalTeam: number}[] = AuxConsumptions.reduce((acc: {Total: number, Currency: string, TotalOwner: number, TotalTeam: number}[], consumption: ProtocolConsumption) => {
        const additionalOwner = (consumption.conceptValues ?? []).reduce((sum, cv) => {
            const calculated = calculateConceptNetAmounts(consumption, cv);
            return sum + calculated.ownerNet;
        }, 0);
        const additionalTeam = (consumption.conceptValues ?? []).reduce((sum, cv) => {
            const calculated = calculateConceptNetAmounts(consumption, cv);
            return sum + calculated.teamNet;
        }, 0);

        const totalOwnerToSettle = (consumption.ownerAmountWithholdingTotal ?? 0) + additionalOwner;
        const totalTeamToSettle = (consumption.teamAmountWithholdingTotal ?? 0) + additionalTeam;
        const totalToSettle = totalOwnerToSettle + totalTeamToSettle;

        const existing = acc.find(item => item.Currency.toUpperCase() === consumption.currencyCode?.toUpperCase());
        if (existing) {
            existing.Total += totalToSettle;
            existing.TotalOwner += totalOwnerToSettle;
            existing.TotalTeam += totalTeamToSettle;
        } else {
            acc.push({
                Total: totalToSettle,
                Currency: consumption.currencyCode,
                TotalOwner: totalOwnerToSettle,
                TotalTeam: totalTeamToSettle,
            });
        }
        return acc;
    }, []);

    // calculate concept totals by concept + currency (net of withholding)
    const AuxTotals: {ConceptName: string, CurrencyValue: string, Total: number, TotalOwner: number, TotalTeam: number}[] = AuxConsumptions.reduce((acc: {ConceptName: string, CurrencyValue: string, Total: number, TotalOwner: number, TotalTeam: number}[], consumption: ProtocolConsumption) => {
        (consumption.conceptValues ?? []).forEach((conceptValue) => {
            const calculated = calculateConceptNetAmounts(consumption, conceptValue);
            const ownerNet = calculated.ownerNet;
            const teamNet = calculated.teamNet;
            const totalNet = calculated.totalNet;

            const existing = acc.find(item =>
                item.ConceptName === conceptValue.contractConceptName
                && item.CurrencyValue.toUpperCase() === conceptValue.currencyCode?.toUpperCase()
            );
            if (existing) {
                existing.Total += totalNet;
                existing.TotalOwner += ownerNet;
                existing.TotalTeam += teamNet;
            } else {
                acc.push({ 
                    ConceptName: conceptValue.contractConceptName, 
                    CurrencyValue: conceptValue.currencyCode, 
                    Total: totalNet,
                    TotalOwner: ownerNet,
                    TotalTeam: teamNet,
                });
            }
        });
        return acc;
    }, []);

    

    return {
        isLoading: state.MedicalProtocols?.isLoadingTotals || false,
        successLoading: state.MedicalProtocols?.LoadingTotalsSuccess || undefined,
        error: state.MedicalProtocols?.ErrorGettingTotals || null,
        Consumptions: AuxConsumptions || [],
        Totals: AuxTotals || [],
        TotalByCurrency: AuxConceptTotalByCurrency,
        protocol: AuxProtocol,
        contract: AuxContract
    }
};

export const mapDispatchToProps = {
    ...ProtocolsStore.actionCreators,
    ...ProtocolConsumptionStore.ActionCreators,
};


export const GetStatusDescription = (status: number) => {

    /*
        NotSettled = 0,
        Pending = 1,
        Payee = 2,
        Settled = 3,
        Rejected = 4,
        Ignored = 5,
        ToSettle = 6
     */

    switch (status) {
        case 0:
            return 'Pendiente de liquidación';
        case 1:
            return 'Con saldo pendiente';
        case 2:
            return 'Pagado';
        case 3:
            return 'Liquidado';
        case 4:
            return 'Rechazado';
        case 5:
            return 'Ignorado';
        case 6:
            return 'Para liquidar';
        default:
            return 'Unknown';
    }
}